import React from 'react'
import { GrFormCheckmark, GrFormClose } from 'react-icons/gr'
import { Paragraph } from '../typo/paragraph'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import Trans from 'next-translate/Trans'
import { Header3 } from '@app/components/general/header'
import { companyName } from '@app/configs'
import { FILTERED_MARKETING } from '@app/configs/app-config'

type TableRowProps = {
  rows: string[]
  identifier?: string
}

const TableHeading = ({ title }: { title: string }) => (
  <thead className='border-b dark:border-gray-600 text-left w-full'>
    <tr>
      <th scope='col' className='text-base font-medium py-4 px-6'>
        {title}
      </th>
    </tr>
  </thead>
)

const tableBorderClass = 'overflow-auto border rounded dark:border-gray-600'

const TableRow = ({ rows }: TableRowProps) => (
  <tr className='border-b dark:bg-gray-800 dark:border-gray-600 text-left'>
    {rows.map((row: string, i: number) => {
      return (
        <td
          key={`table-r-${i}`}
          className={'px-6 py-2.5 whitespace-nowrap text-sm md:text-base'}
        >
          {row === 'true' || row === 'maybe' ? (
            <GrFormCheckmark
              className={`grIcon text-2xl ${
                row === 'maybe' ? 'text-yellow-500' : 'text-green-500'
              }`}
              aria-label={`feature included${
                row === 'maybe' ? ' partially' : ''
              }`}
            />
          ) : row === 'false' ? (
            <GrFormClose
              className='grIcon text-red-500 text-2xl'
              aria-label={'feature excluded'}
            />
          ) : (
            row
          )}
        </td>
      )
    })}
  </tr>
)

// const tableIds = ['feat', 'perf', 'coverage']
// const featID = tableIds[0]
// const perfId = tableIds[1]
// const coverageId = tableIds[2]

const perfList = new Array(4).fill(0)

// list features
function FeatureTables({ header4 }: { header4?: boolean }) {
  const { t } = useTranslation('why-us')
  const featList: string[] = t('feat-list', {}, { returnObjects: true })

  return (
    <>
      <Header3 header4={header4}>{t('features')}</Header3>
      <div className={tableBorderClass}>
        <table className='min-w-full table-auto'>
          <TableHeading title={'Feature'} />
          <tbody>
            {featList.map((item, i) => (
              <TableRow rows={[item, 'true']} key={`tlist-${i}`} />
            ))}
          </tbody>
          <caption className={'p-2 caption-bottom'}>
            {t('feature-compare')}
          </caption>
        </table>
      </div>

      <div className='py-8'>
        <h3 className='font-semibold text-base md:text-lg'>
          {t('performance')}
        </h3>
        <Paragraph>{t('performance-details')}</Paragraph>
      </div>

      <div className={tableBorderClass}>
        <table className='min-w-full table-auto'>
          <TableHeading title={t('performance-cost')} />
          <tbody>
            {perfList.map((_, i) =>
              FILTERED_MARKETING && [3, 4].includes(i) ? null : (
                <TableRow
                  rows={[t(`perf-list.${i}.key`), t(`perf-list.${i}.val`)]}
                  key={`perfList-${i}`}
                />
              )
            )}
          </tbody>
          <caption className={'p-2 caption-bottom'}>
            {t('performance-compare')}
          </caption>
        </table>
      </div>

      {FILTERED_MARKETING ? null : (
        <div className='py-8'>
          <h4 className='font-semibold text-base md:text-lg'>{t('reasons')}</h4>
          <Trans
            values={{ companyName }}
            i18nKey='why-us:reasons-details'
            components={[
              <Paragraph key='rp1' />,
              <a
                href='https://github.com/a11ywatch/crawler'
                rel='noreferrer'
                target='_blank'
                className='text-blue-600 underline hover:text-blue-400'
                key='r1'
              />,
              <a
                href='https://github.com/a11ywatch/kayle'
                rel='noreferrer'
                target='_blank'
                className='text-blue-600 underline hover:text-blue-400'
                key='r2'
              />,
            ]}
          />
        </div>
      )}

      <div className='pt-8'>
        <time dateTime='2023-05-24T012:00:00.000Z'>{t('last-update')}</time>
      </div>
    </>
  )
}

// used on the why-us page
export const MarketingFeatureCompareList = (props: any) => (
  <FeatureTables {...props} />
)
